/**
 * The small pieces the computer's sheets and its page share (slice 6c §7):
 * the state pill and the footers' icons, drawn as the boards draw them.
 */
import type { ReactNode } from "react";
import { cn } from "@crew/ui/lib/utils";

/**
 * The state pill: "LIVE · VIEW ONLY" (a moss dot), "ASLEEP" (a hint dot),
 * "YOU HAVE IT" (`moss`: moss on moss), "DONE" (no dot).
 */
export function DesktopPill({
  dot,
  moss = false,
  children,
}: {
  dot: "moss" | "hint" | null;
  moss?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      data-desktop-pill
      className={cn(
        "inline-flex shrink-0 items-center gap-[7px] rounded-full px-[9px] py-[3px] text-xs leading-4 font-semibold tracking-[0.04em]",
        moss ? "bg-moss/12 text-moss" : "bg-foreground/[0.06] text-foreground",
      )}
    >
      {dot ? (
        <span className={cn("size-[7px] rounded-full", dot === "moss" ? "bg-moss" : "bg-hint")} />
      ) : null}
      {children}
    </span>
  );
}

const STROKE = "var(--color-muted-foreground)";

function Icon({
  size,
  className,
  children,
}: {
  size: number;
  className?: string;
  children: ReactNode;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      fill="none"
      stroke={STROKE}
      strokeWidth="1.8"
      className={cn("shrink-0", className)}
    >
      {children}
    </svg>
  );
}

type IconProps = { size: number; className?: string };

/** View only. */
export const EyeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

/** The sign-in note: what you sign in to stays. */
export const LockIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4" y="10" width="16" height="11" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </Icon>
);

/** A phone's hint: tap and type, or Fit and Zoom. */
export const PhoneIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="7" y="2" width="10" height="20" rx="2" />
    <path d="M3 15a9 9 0 0 0 6 6M21 9a9 9 0 0 0-6-6" />
  </Icon>
);

/** Asleep. */
export const MoonIcon = (p: IconProps & { stroke?: string; strokeWidth?: string }) => (
  <svg
    width={p.size}
    height={p.size}
    viewBox="0 0 24 24"
    aria-hidden
    className={cn("shrink-0", p.className)}
  >
    <path
      d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z"
      fill="none"
      stroke={p.stroke ?? STROKE}
      strokeWidth={p.strokeWidth ?? "1.8"}
    />
  </svg>
);

/** The crew is working: a clock. */
export const ClockIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Icon>
);
